import React, { useState, useEffect } from "react";
import ImgCrop from "antd-img-crop";
import { useSearchParams } from "react-router-dom";
import { Row, Col, Card, Button, Space, Typography, Tabs, Input, Image, Upload, Switch, Table, Tag, Tooltip, Popconfirm, Checkbox } from "antd";
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faUpload, faLock, faUnlock } from "@fortawesome/free-solid-svg-icons";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";

function UserSettings(props) {
  const [userSettings, setUserSettings] = useState(null);
  const [passwordSettings, setPasswordSettings] = useState(null);
  const [userImageProcessing, setUserImageProcessing] = useState(false);
  const [taskBoardPreferences, setTaskBoardPreferences] = useState([]);
  const [authenticatedSenders, setAuthenticatedSenders] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [activeKey, setActiveKey] = useState("user-settings");

  // eslint-disable-next-line
  const [searchParams, setSearchParams] = useSearchParams("0");

  const GetUserSettings = async () => {
    let response = await props.AxiosRequest("/api/user/get/self");
    setUserSettings(response.data.user);
  };
  const GetTaskStatuses = async () => {
    let response = await props.AxiosRequest("/api/task-statuses/get/by-user-preference");
    setTaskStatuses(response.data.task_statuses);
  };
  const GetUserTaskBoardPreferences = async () => {
    let response = await props.AxiosRequest("/api/user/preferences/get");
    setTaskBoardPreferences({ ...response.data.user_preferences, task_board_hide_empty_columns: response.data.user_preferences.hide_empty_columns ? true : false });
  };
  const GetAuthenticatedSenders = async () => {
    let response = await props.AxiosRequest("/api/authenticated-senders/get-owned");
    setAuthenticatedSenders(
      response.data.authenticated_senders.map((m) => {
        return { ...m, key: m.id };
      }),
    );
  };
  const SaveUserSettings = async () => {
    if (!userSettings?.first_name?.length > 0 || !userSettings?.last_name?.length > 0 || !userSettings?.email?.length > 0 || !userSettings?.title?.length > 0)
      return props.MakeNotification("warning", "Please fill in all the required fields.");
    // eslint-disable-next-line
    if (userSettings.email.search(/[a-zA-Z0-9_.\-+[\]"']+@[a-zA-Z0-9_.\-+\[\]"']+[.][a-zA-Z0-9_.\-+\[\]"']+/) === -1) return props.MakeNotification("warning", "Please enter a valid email address.");
    await props.AxiosRequest("/api/user/update/self", { ...userSettings });
    GetUserSettings();
  };
  const SaveNewPassword = async () => {
    if (passwordSettings?.current_password?.length < 8 || passwordSettings?.new_password?.length < 8 || passwordSettings?.confirm_new_password?.length < 8)
      return props.MakeNotification("warning", "Please fill in all the required fields.");
    if (passwordSettings?.new_password !== passwordSettings?.confirm_new_password) return props.MakeNotification("warning", "Please make sure your new password and confirm new password match.");
    await props.AxiosRequest("/api/user/update/password", { ...passwordSettings });
    GetUserSettings();
  };
  const SaveTaskboardOrder = async () => {
    await props.AxiosRequest("/api/user/preferences/update", { ...taskBoardPreferences });
    await props.AxiosRequest("/api/user-preferences/task-order-bulk-update", { task_statuses: taskStatuses });
    GetUserTaskBoardPreferences();
  };
  const DeleteAuthenticatedSender = async (id) => {
    await props.AxiosRequest("/api/authenticated-senders/delete", { id });
    GetAuthenticatedSenders();
  };
  const SetAuthenticatedSenderPrivate = async (id) => {
    await props.AxiosRequest("/api/authenticated-senders/update", { id, is_shared: 0 });
    GetAuthenticatedSenders();
  };
  const SetAuthenticatedSenderPublic = async (id) => {
    await props.AxiosRequest("/api/authenticated-senders/update", { id, is_shared: 1 });
    GetAuthenticatedSenders();
  };

  const OnUserImageChange = async (info) => {
    if (info.file.status === "uploading") return setUserImageProcessing(true);
    if (info.file.status === "done") {
      GetUserSettings();
      props.MakeNotification("success", "Successfully updated your user image. Reload the page to see it take effect on the sidebar.");
    }
  };
  const grid = 8;
  const getListStyle = (isDraggingOver) => ({
    backgroundColor: isDraggingOver ? "lightblue" : "",
    display: "flex",
    transition: "background-color 0.25s ease",
    margin: grid,
  });
  const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    padding: grid * 2,
    margin: `0 ${grid}px 0 0`,

    // change background colour if dragging
    background: isDragging ? "lightgreen" : "",

    // styles we need to apply on draggables
    ...draggableStyle,
  });
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };
  const onDragEnd = async (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const items = reorder(taskStatuses, result.source.index, result.destination.index);
    setTaskStatuses(items);
  };

  const authenticated_sender_table_columns = [
    {
      title: "Email Address",
      dataIndex: "email",
    },
    {
      title: "Authorization Status",
      dataIndex: "is_authenticated",
      render: (value, object) => {
        return value === 1 ? (
          <Tag color="success"> Authenticated</Tag>
        ) : (
          <Tooltip title="This email should have recieved an email to complete the authentication process.">
            <Tag color="warning"> Not Yet Authenticated</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Sharing Status",
      dataIndex: "is_shared",
      render: (value, object) => {
        return value === 1 ? (
          <Tag color="success"> Shared</Tag>
        ) : (
          <Tooltip title="This email can only be used by you in this organization when sending from emails from FirmTamer.">
            <Tag color="warning"> Not Shared</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "right",
      render: (value, object) => {
        return (
          <Space>
            {object.is_shared === 1 ? (
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  SetAuthenticatedSenderPrivate(object.id);
                }}
              >
                <FontAwesomeIcon icon={faLock} className="mr-1" />
                Make Private
              </Button>
            ) : (
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  SetAuthenticatedSenderPublic(object.id);
                }}
              >
                <FontAwesomeIcon icon={faUnlock} className="mr-1" />
                Make Public
              </Button>
            )}
            <Popconfirm
              content="Are you sure you want to delete this authenticated sender?"
              title="Are you sure?"
              onConfirm={() => {
                DeleteAuthenticatedSender(object.id);
              }}
            >
              <Button size="small" danger>
                <FontAwesomeIcon icon={faTrash} className="mr-1" />
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const user_settings_tabs = [
    {
      label: "User Settings",
      key: "user-settings",
      children: (
        <>
          <Typography.Title level={5}>User Settings</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <label htmlFor="input_name">First Name *</label>
              <Input
                id="input_name"
                placeholder="John"
                value={userSettings?.first_name}
                onChange={(e) => {
                  setUserSettings({ ...userSettings, first_name: e.target.value });
                }}
              />
            </Col>
            <Col span={6}>
              <label htmlFor="input_name">Last Name *</label>
              <Input
                id="input_name"
                placeholder="Smith"
                value={userSettings?.last_name}
                onChange={(e) => {
                  setUserSettings({ ...userSettings, last_name: e.target.value });
                }}
              />
            </Col>
            <Col span={6}>
              <label htmlFor="input_name">Email *</label>
              <Input
                id="input_name"
                placeholder="johnsmith@example.com"
                value={userSettings?.email}
                onChange={(e) => {
                  setUserSettings({ ...userSettings, email: e.target.value });
                }}
              />
            </Col>
            <Col span={6}>
              <label htmlFor="input_name">Title *</label>
              <Input
                id="input_name"
                placeholder="User"
                value={userSettings?.title}
                onChange={(e) => {
                  setUserSettings({ ...userSettings, title: e.target.value });
                }}
              />
            </Col>
            <Col span={24}>
              <hr />
            </Col>
            <Col span={6} className="text-center">
              <label htmlFor="input_name">User Image</label>
              <br />
              <br />
              {userSettings?.image_link ? (
                <Image
                  className="mb-2"
                  src={"https://firmtamer-public-bucket.s3.amazonaws.com/" + userSettings?.image_link}
                  alt="avatar"
                  style={{
                    width: "100%",
                    maxWidth: "80px",
                  }}
                />
              ) : (
                <div>
                  {userImageProcessing ? <LoadingOutlined /> : <PlusOutlined />}
                  <div
                    style={{
                      marginTop: 8,
                    }}
                  >
                    Upload
                  </div>
                </div>
              )}
              <br />
              <ImgCrop showGrid aspect={1} minZoom={0.5} quality={1} cropperProps={{ restrictPosition: false }}>
                <Upload
                  action={window.location.protocol + "//" + window.location.hostname + (window.location.port === "3000" ? ":3001" : "") + "/api/user/update-image"}
                  accept=".jpg,.jpeg,.png"
                  name="user_image"
                  headers={{ "x-access-token": props.authToken }}
                  data={{ "x-access-token": props.authToken }}
                  className="avatar-uploader"
                  showUploadList={false}
                  onChange={OnUserImageChange}
                >
                  <Button icon={<FontAwesomeIcon icon={faUpload} />}>Click to Upload a New Logo</Button>
                </Upload>
              </ImgCrop>
            </Col>
            <Col span={24}>
              <hr />
            </Col>
            <Col span={24}>
              <label htmlFor="tutorial_checkbox" className="w-100">
                Show Tutorial on Login *
              </label>
              <Checkbox
                id="tutorial_checkbox"
                checked={userSettings?.welcome_package_show ? true : false}
                onChange={(e) => {
                  setUserSettings({ ...userSettings, welcome_package_show: e.target.checked });
                }}
              />
            </Col>

            <div className="mb-3 col-md-12 text-right">
              <Button type="primary" id="save-company-information" onClick={SaveUserSettings}>
                Save changes
              </Button>
            </div>
          </Row>
        </>
      ),
    },
    {
      label: "Password Settings",
      key: "password-settings",
      children: (
        <>
          <Typography.Title level={5}>Password Settings</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={12} offset={6}>
              <label htmlFor="input_name">Current Password *</label>
              <Input
                id="input_name"
                placeholder="Must be at least 8 characters long"
                type="password"
                value={passwordSettings?.current_password}
                onChange={(e) => {
                  setPasswordSettings({ ...passwordSettings, current_password: e.target.value });
                }}
              />
            </Col>
            <Col span={12} offset={6}>
              <label htmlFor="new_password">New Password *</label>
              <Input
                id="new_password"
                placeholder="Must be at least 8 characters long"
                type="password"
                value={passwordSettings?.new_password}
                onChange={(e) => {
                  setPasswordSettings({ ...passwordSettings, new_password: e.target.value });
                }}
              />
            </Col>
            <Col span={12} offset={6}>
              <label htmlFor="input_name">Confirm New Password *</label>
              <Input
                id="input_name"
                placeholder=""
                type="password"
                value={passwordSettings?.confirm_new_password}
                onChange={(e) => {
                  setPasswordSettings({ ...passwordSettings, confirm_new_password: e.target.value });
                }}
              />
            </Col>

            <div className="mb-3 col-md-12 text-right">
              <Button type="primary" id="save-company-information" onClick={SaveNewPassword}>
                Save New Password
              </Button>
            </div>
          </Row>
        </>
      ),
    },
    {
      label: "Task Board Settings",
      key: "task-board-settings",
      children: (
        <>
          <Typography.Title level={5}>Task Board Settings</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div className="w-100">
                <Tooltip
                  title={
                    <div className="text-center">
                      When enabled, this will hide all task statuses that don't have tasks in them. <br />
                      (Except for the starting task status and ending task status)
                    </div>
                  }
                >
                  <label htmlFor="column_hide">Hide Empty Columns</label>
                </Tooltip>
              </div>

              <Switch
                id="column_hide"
                type="password"
                checkedChildren="Statuses Without Tasks Will Be Hidden"
                unCheckedChildren="All Statuses Will Be Shown"
                checked={taskBoardPreferences?.task_board_hide_empty_columns}
                onChange={(e) => {
                  setTaskBoardPreferences({ ...taskBoardPreferences, task_board_hide_empty_columns: e });
                }}
              />
            </Col>
            <Col span={24}>
              <DragDropContext onDragEnd={onDragEnd}>
                <label htmlFor="new_password">Your Task Board Order</label>
                <Space className="w-100" style={{ overflowX: "scroll" }}>
                  <Droppable droppableId="droppable" direction="horizontal">
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)} {...provided.droppableProps}>
                        {taskStatuses.map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id + ""} index={index}>
                            {(provided, snapshot) => (
                              <Tag ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}>
                                <b>{item.name}</b>
                              </Tag>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </Space>
              </DragDropContext>
            </Col>
            <Col span={24} className="text-right">
              <Button type="primary" id="save-company-information" onClick={SaveTaskboardOrder}>
                Save Task Board Preferences
              </Button>
            </Col>
          </Row>
        </>
      ),
    },
    {
      label: "Authenticated Senders",
      key: "authenticated-sender-settings",
      children: (
        <>
          <Typography.Title level={5}>Authenticated Sender Settings</Typography.Title>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Table columns={authenticated_sender_table_columns} dataSource={authenticatedSenders} />
            </Col>
          </Row>
        </>
      ),
    },
  ];

  useEffect(() => {
    GetUserSettings();
    GetTaskStatuses();
    GetAuthenticatedSenders();
    GetUserTaskBoardPreferences();
    if (user_settings_tabs.filter((f) => f.key === searchParams.get("tab")).length > 0) setActiveKey(searchParams.get("tab"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <main className="content">
        <div className="container-fluid">
          <div className="header">
            <h1 className="header-title">User Settings</h1>
          </div>
          <Card>
            <Tabs tabPosition="left" size="large" activeKey={activeKey} onChange={(newActiveKey) => setActiveKey(newActiveKey)} items={user_settings_tabs} />
          </Card>
        </div>
      </main>
    </>
  );
}

export default UserSettings;
